import { Stack } from '@/components/layout';
import { useSelectContext } from '@/components/ui/select/select-context';
import { SelectItem } from '@/components/ui/select/select-item';
import { useStyles } from '@/components/ui/select/styles';
import type { SelectContentProps } from '@/components/ui/select/types';
import { useBottomInsetSmart, useScaling } from '@/hooks';
import { Device } from '@/theme/scaling';
import { FC, memo, useMemo, useState } from 'react';
import { FlatList, Modal, Platform, Pressable, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const SelectContent: FC<SelectContentProps> = memo(
  ({ style, maxHeight, minWidth, itemTextVariant, children, statusBarTranslucent = false }) => {
    const {
      open,
      setOpen,
      options,
      selectedValue: _selectedValue,
      disabled,
      triggerLayout,
    } = useSelectContext();
    // get status bar height
    const statusBarHeight = Device.statusBarHeight;
    const { scale, deviceWidth, deviceHeight } = useScaling();
    const styles = useStyles();
    const bottomInset = useBottomInsetSmart();
    const { top, bottom } = useSafeAreaInsets();
    // Measured height of the Modal's content area. On edge-to-edge Android, `deviceHeight`
    // (window) includes the status bar + nav bar, but a non-translucent Modal lays out below
    // the status bar and above the nav bar — so its real height is smaller. We measure it
    // instead of guessing the system-bar sizes; falls back to `deviceHeight` for the first frame.
    const [rootHeight, setRootHeight] = useState(0);

    const selectedValue = useMemo(() => {
      const opt = options.find(o => o.value === _selectedValue);
      return opt?.value ?? null;
    }, [_selectedValue, options]);

    if (!open) return null;

    const resolvedMaxHeight = maxHeight ?? scale(320);
    const modalHorizontalPadding = scale(12);
    const triggerGap = Platform.OS === 'android' ? top + bottom : 0;
    const edgePadding = scale(8);

    const fallbackWidth = deviceWidth - modalHorizontalPadding * 2;
    const contentWidth = Math.min(
      Math.max(triggerLayout?.width ?? fallbackWidth, minWidth ?? 0),
      deviceWidth - edgePadding * 2,
    );
    const triggerX = triggerLayout?.x ?? modalHorizontalPadding;
    const triggerY = triggerLayout?.y ?? scale(120);
    const triggerHeight = triggerLayout?.height ?? scale(48);

    // `measureInWindow` returns screen-relative coordinates (measured from the very top of
    // the screen, above the status bar). A non-translucent Android Modal lays out its content
    // BELOW the status bar, so its (0,0) is shifted down by the status bar height. Convert the
    // trigger position into the Modal's own coordinate space so the dropdown lines up with the
    // trigger. iOS Modals cover the full screen, so triggerY is already in Modal space there.
    const statusBarOffset =
      Platform.OS === 'android' && !statusBarTranslucent ? statusBarHeight : 0;
    const triggerTop = triggerY - statusBarOffset;

    const clampedLeft = Math.max(
      edgePadding,
      Math.min(triggerX, deviceWidth - contentWidth - edgePadding),
    );

    // Use the measured Modal height once available; deviceHeight is only a first-frame fallback.
    const containerHeight = rootHeight || deviceHeight;

    const belowTop = triggerTop + triggerHeight + triggerGap;
    const spaceBelow = Math.max(
      0,
      containerHeight - edgePadding - belowTop - bottomInset - scale(16),
    );
    // Safe top margin in Modal space: the notch on iOS / status bar when translucent; on a
    // non-translucent Android Modal the status bar is already excluded, so it collapses to 0.
    const safeTopInModal = top - statusBarOffset;
    const spaceAbove = Math.max(0, triggerTop - triggerGap - edgePadding - safeTopInModal);
    const placeBelow =
      spaceBelow >= Math.min(scale(240), resolvedMaxHeight) || spaceBelow >= spaceAbove;
    const availableHeight = Math.max(
      scale(220),
      placeBelow
        ? spaceBelow - (Platform.select({ ios: 0, android: triggerHeight }) ?? 0)
        : spaceAbove,
    );
    const displayedMaxHeight = Math.min(resolvedMaxHeight, availableHeight);
    const verticalAnchorStyle = placeBelow
      ? { top: belowTop }
      : {
          // Anchor by bottom when placing above trigger, so short content still hugs trigger.
          // containerHeight is the Modal's real content height; subtract the Modal-space trigger top.
          bottom: containerHeight - triggerTop - triggerGap,
        };

    return (
      <Modal
        transparent
        visible={open}
        animationType="fade"
        supportedOrientations={['portrait', 'landscape']}
        onRequestClose={() => setOpen(false)}
        statusBarTranslucent={statusBarTranslucent}>
        <Stack style={styles.modalRoot} onLayout={e => setRootHeight(e.nativeEvent.layout.height)}>
          <Pressable style={styles.modalBackdrop} onPress={() => setOpen(false)} />

          <Stack
            style={[
              styles.modalContentContainer,
              verticalAnchorStyle,
              { left: clampedLeft, width: contentWidth, maxHeight: displayedMaxHeight },
            ]}>
            <Stack style={[styles.contentWrapper, { maxHeight: displayedMaxHeight }, style]}>
              {children ? (
                <ScrollView
                  keyboardShouldPersistTaps="handled"
                  contentContainerStyle={styles.contentScroll}>
                  {children}
                </ScrollView>
              ) : (
                <FlatList
                  data={options}
                  keyExtractor={item => item.value}
                  keyboardShouldPersistTaps="handled"
                  renderItem={({ item }) => (
                    <SelectItem
                      value={item.value}
                      label={item.label}
                      style={undefined}
                      disabled={disabled}
                      isSelected={(item.value ?? '') === (selectedValue ?? '')}
                      itemTextVariant={itemTextVariant}
                    />
                  )}
                  scrollEnabled={options.length > 0}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    );
  },
);

SelectContent.displayName = 'Select.Content';
